import {
  InfoCompletenessScoring,
  RobotsBot,
  RobotsRuleResponse,
  TechRuleFinding,
} from 'store/api/apiTypes';
import {
  TECHNICAL_ACCESSIBILITY_RULE_KEYS,
  TECHNICAL_ACCESSIBILITY_UNABLE_TO_CHECK_VALUE,
  normalizeTechnicalRuleFindings,
  TechnicalAccessibilityRuleType,
} from 'shared/helpers/technicalAccessibility';
import { resolveMaxScore } from 'shared/helpers/ruleScoring';

type TechnicalRuleLevel = 'high' | 'medium' | 'low' | 'error';

const isUnableToCheckValue = (
  value: unknown
): value is typeof TECHNICAL_ACCESSIBILITY_UNABLE_TO_CHECK_VALUE =>
  value === TECHNICAL_ACCESSIBILITY_UNABLE_TO_CHECK_VALUE;

const getTextByScore = (
  score: number | null | undefined,
  max_score: number
): TechnicalRuleLevel => {
  if (score === null || score === undefined) {
    return 'error';
  }

  const percentage = max_score > 0 ? score / max_score : 0;

  if (percentage >= 0.8) {
    return 'high';
  } else if (percentage >= 0.4) {
    return 'medium';
  } else {
    return 'low';
  }
};

const RULES_WITH_ERROR_COPY: Set<string> = new Set([
  ...TECHNICAL_ACCESSIBILITY_RULE_KEYS.llms,
  ...TECHNICAL_ACCESSIBILITY_RULE_KEYS.schema,
]);

const getForcedErrorRuleKeys = (
  ruleType: TechnicalAccessibilityRuleType,
  findings: Record<string, TechRuleFinding>
): Set<string> => {
  const forcedErrorRuleKeys = new Set<string>();

  if (ruleType === 'llms' && isUnableToCheckValue(findings.file?.exists)) {
    TECHNICAL_ACCESSIBILITY_RULE_KEYS.llms.forEach(key =>
      forcedErrorRuleKeys.add(key)
    );
  }

  if (ruleType === 'schema' && isUnableToCheckValue(findings.schema?.exists)) {
    TECHNICAL_ACCESSIBILITY_RULE_KEYS.schema.forEach(key =>
      forcedErrorRuleKeys.add(key)
    );
  }

  return forcedErrorRuleKeys;
};

const resolveRuleLevel = (
  key: string,
  score: number | null | undefined,
  max_score: number | null | undefined,
  findings: Record<string, unknown>
): TechnicalRuleLevel => {
  if (RULES_WITH_ERROR_COPY.has(key) && isUnableToCheckValue(findings.exists)) {
    return 'error';
  }

  const normalizedMaxScore = resolveMaxScore(max_score);
  const level = getTextByScore(score, normalizedMaxScore);
  if (level !== 'error') {
    return level;
  }
  return RULES_WITH_ERROR_COPY.has(key) ? 'error' : 'low';
};

const normalizeRuleScore = (score: number | null | undefined): number =>
  score ?? 0;

const getRobotsBotAccessState = (bot?: RobotsBot): boolean => {
  const hasRestrictedPaths = (bot?.blocks_paths?.length ?? 0) > 0;
  return Boolean(bot?.allowed) && !hasRestrictedPaths;
};

const getRobotsRuleLevel = (bots: boolean[]): TechnicalRuleLevel => {
  const passedBots = bots.filter(Boolean).length;
  if (passedBots === bots.length) {
    return 'high';
  }
  if (passedBots === 0) {
    return 'low';
  }
  return 'medium';
};

const getTitleKey = (key: string): string => {
  const titleKeyMap: Record<string, string> = {
    products: 'products_section',
    schema: 'schema_exists',
  };
  return titleKeyMap[key] || key;
};

const TECHNICAL_BOOLEAN_FINDINGS_KEYS: Record<
  string,
  Record<string, string>
> = {
  structure: {
    h1_present: 'h1_present',
    blockquote_present: 'blockquote_present',
    h2_present: 'h2_present',
    links_present: 'links_present',
  },
  products: {
    products_present: 'products_present',
    services_present: 'services_present',
  },
};

const normalizeBooleanFindings = (
  findings: Record<string, unknown>,
  keysMap: Record<string, string>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(keysMap)
      .map(([field, translationKey]) => [translationKey, findings[field]])
      .filter(([, value]) => typeof value === 'boolean')
  );

const normalizeTechnicalFindings = (
  key: string,
  findings: Record<string, unknown>
): Record<string, unknown> => {
  const keysMap = TECHNICAL_BOOLEAN_FINDINGS_KEYS[key];
  if (keysMap) {
    const normalized = normalizeBooleanFindings(findings, keysMap);
    return Object.keys(normalized).length > 0 ? normalized : findings;
  }

  return findings;
};

export const getTotalMaxScore = (
  findings: Record<string, TechRuleFinding>
): number =>
  Object.values(findings).reduce((sum, finding) => {
    return sum + resolveMaxScore(finding.max_score, 0);
  }, 0);

export const techRuleFindingToInfoCompletenessScore = (
  findings: Record<string, TechRuleFinding>,
  ruleType: TechnicalAccessibilityRuleType
): Record<string, InfoCompletenessScoring> => {
  const result: Record<string, InfoCompletenessScoring> = {};
  const normalizedFindings = normalizeTechnicalRuleFindings(findings, ruleType);
  const forcedErrorRuleKeys = getForcedErrorRuleKeys(
    ruleType,
    normalizedFindings
  );

  Object.entries(normalizedFindings).forEach(([key, finding]) => {
    const { score, max_score, ...otherFindings } = finding;
    const normalizedMaxScore = resolveMaxScore(max_score);
    const level = forcedErrorRuleKeys.has(key)
      ? 'error'
      : resolveRuleLevel(key, score, max_score, otherFindings);
    const normalizedScore = level === 'error' ? 0 : normalizeRuleScore(score);
    const titleKey = getTitleKey(key);
    const normalizedRuleFindings = normalizeTechnicalFindings(
      key,
      otherFindings
    );

    result[titleKey] = {
      score: normalizedScore,
      max_score: normalizedMaxScore,
      weight: 1.0,
      level,
      rule_applied: key,
      findings: normalizedRuleFindings,
      findings_text: `findings_${key}_${level}`,
      what_is_missing: `what_is_missing_${key}_${level}`,
      how_to_improve: `how_to_improve_${key}_${level}`,
      reasoning: `reasoning_${key}_${level}`,
      status_title: `${key}_status_title_${level}`,
      status_description: `${key}_status_description_${level}`,
    };
  });

  return result;
};

export const robosRuleToInfoCompletenessScore = (
  robotsRule: RobotsRuleResponse
): Record<string, InfoCompletenessScoring> => {
  const { score, max_score, rule, findings } = robotsRule;
  const robotsFileExists = findings?.file?.exists;
  const {
    gpt_bot,
    chat_gpt_user,
    claude_bot,
    google_extended,
    perplexity_bot,
  } = findings || {};

  const gptBotAccess = getRobotsBotAccessState(gpt_bot);
  const claudeBotAccess = getRobotsBotAccessState(claude_bot);
  const googleExtendedAccess = getRobotsBotAccessState(google_extended);
  const perplexityBotAccess = getRobotsBotAccessState(perplexity_bot);
  const chatGptUserAccess = getRobotsBotAccessState(chat_gpt_user);

  const normalizedMaxScore = resolveMaxScore(max_score);
  const level = isUnableToCheckValue(robotsFileExists)
    ? 'error'
    : getRobotsRuleLevel([
        gptBotAccess,
        claudeBotAccess,
        googleExtendedAccess,
        perplexityBotAccess,
        chatGptUserAccess,
      ]);
  const normalizedScore = level === 'error' ? 0 : normalizeRuleScore(score);

  const robotsFindings = {
    gpt_bot: gptBotAccess,
    claude_bot: claudeBotAccess,
    google_extended: googleExtendedAccess,
    perplexity_bot: perplexityBotAccess,
    chat_gpt_user: chatGptUserAccess,
  };

  return {
    'robots-txt': {
      score: normalizedScore,
      max_score: normalizedMaxScore,
      weight: 1.0,
      level,
      rule_applied: rule,
      findings:
        level === 'error'
          ? { robots_txt_exists: robotsFileExists }
          : robotsFindings,
      findings_text: `findings_robots-txt_${level}`,
      what_is_missing: `what_is_missing_robots-txt_${level}`,
      how_to_improve: `how_to_improve_robots-txt_${level}`,
      reasoning: `reasoning_robots-txt_${level}`,
      status_title: `robots_txt_status_title_${level}`,
      status_description: `robots_txt_status_description_${level}`,
    },
  };
};
